import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { EMPLOYMENT_FAQ_CATEGORIES } from "@/content/employment-corpus/faq-defs";

export const Route = createFileRoute("/employment/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/employment-corpus");
    return { faqs: m.EMPLOYMENT_FAQ_TH };
  },
  component: EmploymentFaqHub,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `รับรองเอกสารสมัครงาน/สมัครทุนต่างประเทศ — FAQ ${faqs.length} ข้อ`,
        description:
          "คำถามที่พบบ่อยเรื่องรับรองเอกสารเพื่อสมัครงานและสมัครทุนต่างประเทศ: หนังสือรับรองการทำงาน วุฒิการศึกษา ใบอนุญาตประกอบวิชาชีพ Certificate of Good Standing การประเมินทักษะ VETASSESS/WES/ENIC-NARIC ใบตรวจประวัติอาชญากรรม คำแปล NAATI/sworn นิติกรณ์กรมการกงสุลและการรับรองสถานทูต",
        path: "/employment/faq",
        type: "article",
        alternates: [{ lang: "en", path: "/en/employment/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "FAQ รับรองเอกสารสมัครงาน/ทุนต่างประเทศ", url: `${site}/employment/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/employment/faq")) },
      ],
    };
  },
});

function EmploymentFaqHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <span>FAQ รับรองเอกสารสมัครงาน/ทุนต่างประเทศ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          รับรองเอกสารเพื่อสมัครงานและสมัครทุนต่างประเทศ — คำถามที่พบบ่อย {faqs.length} ข้อ
        </h1>
        <p className="mb-6 text-muted-foreground" data-speakable="true">
          คำตอบอ้างอิงแนวปฏิบัติของกองสัญชาติและนิติกรณ์ กรมการกงสุล (consular.mfa.go.th) กรมการจัดหางาน (doe.go.th)
          สำนักงาน ก.พ. (ocsc.go.th) สำนักงานปลัดกระทรวงการอุดมศึกษา วิทยาศาสตร์ วิจัยและนวัตกรรม (mhesi.go.th)
          กองทะเบียนประวัติอาชญากร สำนักงานตำรวจแห่งชาติ สภาวิชาชีพในประเทศไทย และหน่วยประเมินคุณวุฒิต่างประเทศ
          ได้แก่ NAATI, VETASSESS, Engineers Australia, ACS, WES และเครือข่าย ENIC-NARIC โดยอนุสัญญา Apostille
          จะมีผลใช้บังคับกับประเทศไทยวันที่ 28 กุมภาพันธ์ 2570 ข้อมูลนี้เป็นข้อมูลทั่วไป ระยะเวลาและเงื่อนไขเปลี่ยนแปลงได้
          โปรดยืนยันกับหน่วยงานปลายทางก่อนยื่นทุกครั้ง
        </p>
        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>English:</strong>{" "}
          <a href="/en/employment/faq" className="text-primary underline">
            Documents for overseas jobs &amp; scholarships FAQ →
          </a>
        </div>
        <section aria-label="หมวดคำถาม" className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">เลือกอ่านตามหมวด</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {EMPLOYMENT_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/employment/faq/${c.slug}`} className="rounded-lg border bg-card p-3 text-sm transition hover:border-primary hover:shadow-sm">
                <div className="mb-1 font-semibold">{c.titleTh}</div>
                <div className="line-clamp-2 text-xs text-muted-foreground">{c.descTh}</div>
              </a>
            ))}
          </div>
        </section>
        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="mr-2 text-muted-foreground">ข้อ {i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
        <section aria-label="คลัสเตอร์ที่เกี่ยวข้อง" className="mt-10">
          <h2 className="mb-3 text-xl font-bold">อ่านต่อในเรื่องที่เกี่ยวข้อง</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            <a href="/education/faq" className="rounded-full border bg-muted/40 px-3 py-1 hover:bg-muted">FAQ รับรองวุฒิการศึกษา</a>
            <a href="/translation/faq" className="rounded-full border bg-muted/40 px-3 py-1 hover:bg-muted">FAQ แปลเอกสารรับรอง</a>
            <a href="/police-clearance/faq" className="rounded-full border bg-muted/40 px-3 py-1 hover:bg-muted">FAQ ตรวจประวัติอาชญากรรม</a>
            <a href="/consular-legalization/faq" className="rounded-full border bg-muted/40 px-3 py-1 hover:bg-muted">FAQ นิติกรณ์กงสุล/Apostille</a>
            <a href="/notary/faq" className="rounded-full border bg-muted/40 px-3 py-1 hover:bg-muted">FAQ ทนายรับรองลายมือชื่อ</a>
            <a href="/visa/faq" className="rounded-full border bg-muted/40 px-3 py-1 hover:bg-muted">FAQ วีซ่าและตรวจคนเข้าเมือง</a>
          </div>
        </section>
        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">ให้เราจัดชุดเอกสารสมัครงานหรือสมัครทุนให้</h2>
          <p className="text-sm text-muted-foreground">
            แจ้งประเทศปลายทาง หน่วยงานผู้รับ และกำหนดวันปิดรับสมัคร ทางโทรศัพท์ LINE หรืออีเมล จันทร์–เสาร์ 9:00–18:00
            เจ้าหน้าที่จะจัดลำดับขั้นรับรองให้เหมาะกับอายุเอกสารและแจ้งค่าบริการก่อนเริ่มงาน
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">ติดต่อเจ้าหน้าที่</a>
        </section>
        <FaqSourceNote topic="employment" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}